import { useCompanyFeature } from 'utils/useCompanyFeature';
import Settings from '@mui/icons-material/Settings';
import { Box, Button, Toolbar } from '@mui/material';
import ChartIcon from 'assets/icons/chart.svg';
import MessagesIcon from 'assets/icons/messages.svg';
import PeopleIcon from 'assets/icons/people.svg';
import AppsIcon from 'mdi-react/AppsIcon';
import ClockOutlineIcon from 'mdi-react/ClockOutlineIcon';
import FileIcon from 'mdi-react/FileIcon';
import { bool } from 'prop-types';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { Link } from 'react-router';
import { LAUNCHER_APP_ID } from 'services/constants';
import { useGetQuery } from 'store/app/entities/AppDataSlice';
import authCompanySelector from 'store/selectors/authCompany';
import isImpersonatingSelector from 'store/selectors/isImpersonating';
import isSuperAdminSelector from 'store/selectors/isSuperAdmin';
import isTeamAdminSelector from 'store/selectors/isTeamAdmin';
import CompaniesDropdown from './CompaniesDropdown';
import HeaderTab from './HeaderTab';
import HeaderTabs from './HeaderTabs';
import Logo from './Logo';
import MenuIcon from './MenuIcon';
import Separator from './Separator';
import UserMenu from './UserMenu';

const TIME_TRACKING_APP = '5fe8c4103beba1318a4cfb66';

const headerSx = theme => ({
  color: theme.palette.primary.ultraDark,
  backgroundColor: theme.palette.background.paper,
  boxShadow: `inset 0 -1px 0 0 ${theme.palette.divider}`,
  '@media print': {
    display: 'none',
  },
});

const toolbarSx = {
  justifyContent: 'center',
};

const leftItemsSx = {
  display: 'flex',
  alignItems: 'center',
};

const rightItemsSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  textAlign: 'right',
};

const settingsSx = {
  color: 'primary.main',
  display: 'flex',
  padding: '12px',
};

const Header = ({ hideNavigation = false }) => {
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const isTeamAdmin = useSelector(isTeamAdminSelector);
  const authCompany = useSelector(authCompanySelector);
  const isImpersonating = useSelector(isImpersonatingSelector);
  const areDocumentsEnabled = useCompanyFeature('documents');
  const locale = useSelector(state => state.i18n.locale);
  const isLauncherAppInstalled = !!authCompany?.apps?.includes(LAUNCHER_APP_ID);
  const { data } = useGetQuery(
    { appId: LAUNCHER_APP_ID, include: 'launcher-links' },
    { skip: !isLauncherAppInstalled },
  );

  return (
    <Box component="header" sx={headerSx}>
      <Toolbar sx={toolbarSx}>
        <Box sx={leftItemsSx}>
          <MenuIcon />
          <Logo />
          <Separator />
          <CompaniesDropdown />
        </Box>
        {!hideNavigation ? (
          <HeaderTabs>
            <HeaderTab value="/home/messages" icon={<MessagesIcon />}>
              {I18n.t('Header.tabs.Messages')}
            </HeaderTab>
            <HeaderTab value="/home/people" visible={isTeamAdmin} icon={<PeopleIcon />}>
              {I18n.t('Header.tabs.People')}
            </HeaderTab>
            <HeaderTab
              value="/home/time-tracking"
              visible={isTeamAdmin && authCompany.apps?.includes(TIME_TRACKING_APP)}
              icon={<ClockOutlineIcon />}
            >
              {I18n.t('Header.tabs.TimeTracking')}
            </HeaderTab>
            <HeaderTab value="/home/analytics" visible={isTeamAdmin} icon={<ChartIcon />}>
              {I18n.t('Header.tabs.Analytics')}
            </HeaderTab>
            <HeaderTab
              value="/home/documents"
              icon={<FileIcon />}
              visible={areDocumentsEnabled || isSuperAdmin}
            >
              {I18n.t('Header.tabs.Documents')}
            </HeaderTab>
            <HeaderTab
              value="/home/launcher"
              icon={<AppsIcon />}
              visible={isLauncherAppInstalled && data?.active}
              data={data}
            >
              {data?.name?.[locale] || I18n.t('Header.tabs.Launcher')}
            </HeaderTab>
          </HeaderTabs>
        ) : null}
        <Box sx={rightItemsSx}>
          {!hideNavigation && isTeamAdmin && (
            <Box component={Link} to="/home/settings/general" sx={settingsSx}>
              <Settings />
            </Box>
          )}
          {!!isSuperAdmin && !isImpersonating && (
            <Button variant="text" sx={{ marginRight: 2 }}>
              <Link to="admin" data-testid="superadmin-nav">
                Admin
              </Link>
            </Button>
          )}
          <UserMenu disabled={hideNavigation} />
        </Box>
      </Toolbar>
    </Box>
  );
};

Header.propTypes = {
  hideNavigation: bool,
};

export default memo(Header);
